import React, { useState, useRef, useMemo, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { PlusCircle, Upload, Download, X, Trash2, ChevronLeft, ChevronRight, Search, Edit } from "lucide-react";
import { CLIENT_COLUMNS, DROPDOWN_FIELDS, INITIAL_STATE } from "../utils/constants";
import EditRecordModal from "./EditRecordModal";
import {
    calculateShift, calculateTAT, formatDateOnly,
    calculateSLA, calculateValueTenPercent, getDayName, formatDateTime, getStatusFromSubstatus,
    parseCustomDate, getActiveRowColor, calculateActiveSLA
} from "../utils/helpers";

const toTitleCase = (str) => {
    if (!str) return "";
    if (str === "date_column") return "Date";
    if (str === "pp") return "Purchase Price";
    if (str === "tat") return "SLA TAT";
    if (str === "reviewer_tat") return "Reviewer TAT";
    return str
        .replace(/_/g, " ")
        .split(" ")
        .map(word => {
            const lower = word.toLowerCase();
            const uppercaseAcronyms = ["sla", "tat", "qc", "pp", "fha", "usda", "va", "vip"];
            if (uppercaseAcronyms.includes(lower)) {
                return word.toUpperCase();
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(" ");
};

const FileTable = ({
    files = [],
    setFiles, activeDept, activeClient, currentUser, isManager, setShowManualModal, fetchData,
    clientTatMap = {},
    isAdmin = false
}) => {
    const fileInputRef = useRef(null);
    const containerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [modalFile, setModalFile] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(null);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [toast, setToast] = useState(null);
    const toastTimeoutRef = useRef(null);
    const [revisionsModal, setRevisionsModal] = useState({ show: false, text: "", fileNumber: "" });

    const handleShowRevisionsModal = (text, fileNumber) => {
        setRevisionsModal({ show: true, text, fileNumber });
    };

    const showToast = (message, type = "success") => {
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }
        setToast({ message, type });
        toastTimeoutRef.current = setTimeout(() => {
            setToast(null);
        }, 4000);
    };

    const handleMouseDown = (e) => {
        const tagName = e.target.tagName.toLowerCase();
        if (
            tagName === "input" ||
            tagName === "select" ||
            tagName === "textarea" ||
            tagName === "svg" ||
            tagName === "path" ||
            e.target.closest("select") ||
            e.target.closest("input") ||
            e.target.closest("textarea") ||
            e.target.closest("svg") ||
            e.target.closest("button") ||
            e.target.closest("td")
        ) {
            return;
        }
        setIsDragging(true);
        setStartX(e.pageX - containerRef.current.offsetLeft);
        setScrollLeft(containerRef.current.scrollLeft);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - containerRef.current.offsetLeft;
        const walk = (x - startX) * 1.5;
        containerRef.current.scrollLeft = scrollLeft - walk;
    };

    const [activeStatus, setActiveStatus] = useState("ALL OPEN");
    const [subFilter, setSubFilter] = useState("Total");
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [bulkEmployee, setBulkEmployee] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
    const [dynamicEmployees, setDynamicEmployees] = useState([]);

    // Import loading and progress states
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const itemsPerPage = 100;

    const duplicateFileNumbers = useMemo(() => {
        const counts = {};
        files.forEach(f => {
            const num = String(f.file_number || f.loan_number || "").trim().toLowerCase();
            if (num && num !== "-") {
                counts[num] = (counts[num] || 0) + 1;
            }
        });
        return new Set(
            Object.keys(counts).filter(num => counts[num] > 1)
        );
    }, [files]);

    // ✨ REQUIRED LOGIC CHANGE: Filter out and set a strictly limited column set whenever activeDept is "Revised"
    const currentColumns = useMemo(() => {
        let cols = [];
        if (activeDept === "Revised") {
            cols = [
                "date_column",
                "loan_number",
                "assigned_to",
                "location",
                "client_name",
                "priority",
                "address",
                "in_time",
                "assign_time",
                "out_time",
                "tat",
                "reviewer_tat",
                "sla_status",
                "status",
                "substatus",
                "revisions",
                "notes_message"
            ];
        } else {
            const baseCols = CLIENT_COLUMNS[activeClient] || Object.keys(INITIAL_STATE);
            const hasFileNum = baseCols.includes("file_number");
            const hasLoanNum = baseCols.includes("loan_number");
            const fileCol = hasFileNum ? "file_number" : (hasLoanNum ? "loan_number" : null);

            const filtered = baseCols.filter(c => c !== "sr_no" && c !== "date_column" && c !== fileCol);
            const prefix = ["date_column"];
            if (fileCol) prefix.push(fileCol);

            cols = [...prefix, ...filtered];
        }
        return cols;
    }, [activeDept, activeClient]);


    // ✅ Synchronize active database names array from backend on view load
    useEffect(() => {
        const fetchActiveEmployeeList = async () => {
            try {
                const res = await axios.get("/api/users/dropdown-list");
                if (res.data && Array.isArray(res.data)) {
                    setDynamicEmployees(res.data);
                }
            } catch (err) {
                console.error("Failed to parse runtime user profiles list:", err);
                if (DROPDOWN_FIELDS && DROPDOWN_FIELDS.assigned_to) {
                    setDynamicEmployees(DROPDOWN_FIELDS.assigned_to);
                }
            }
        };
        fetchActiveEmployeeList();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
        if (typeof fetchData === "function") {
            fetchData(false);
        }
    }, [activeDept, activeClient, searchTerm]);

    const formatDateForInput = (value) => {
        if (!value) return "";

        const str = String(value).trim();

        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
            return str;
        }

        if (str.includes("T")) {
            return str.split("T")[0];
        }

        const d = new Date(str);
        if (isNaN(d.getTime())) return "";

        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };


    const getDbUpdatePayload = (key, value, updatedRecord) => {
        if (key === "notes" || key === "notes_message") {
            return { notes_message: value };
        }

        if (key === "reviewer_name" || key === "Reviewer_Name" || key === "reviewer") {
            return { reviewer_name: value };
        }

        if (key === "substatus") {
            return {
                substatus: updatedRecord.substatus || value,
                status: updatedRecord.status || ""
            };
        }

        if (key === "status") {
            return { status: value };
        }

        if (key === "in_time") {
            return {
                in_time: value,
                tat: updatedRecord.tat || "",
                shift: updatedRecord.shift || "",
                sla_status: updatedRecord.sla_status || ""
            };
        }

        if (key === "out_time") {
            return {
                out_time: value,
                tat: updatedRecord.tat || "",
                sla_status: updatedRecord.sla_status || ""
            };
        }

        if (key === "date_column") {
            return {
                date_column: value,
                day: updatedRecord.day || ""
            };
        }

        if (key === "pp" || key === "market_value") {
            return {
                [key]: value,
                ten_percent: updatedRecord.ten_percent || ""
            };
        }

        if (key === "file_number" || key === "loan_number") {
            return {
                file_number: value,
                loan_number: value
            };
        }

        return { [key]: value };
    }; const handleModalSave = async (updatedFields) => {
        if (!modalFile) return;

        const targetId = modalFile.id || modalFile._id;
        const statusLower = String(updatedFields.status || "").trim().toLowerCase();

        const bypassValidation =
            statusLower === "working" ||
            statusLower === "wrong report" ||
            statusLower === "do not review" ||
            statusLower === "complete by client" ||
            statusLower.includes("wrong report") ||
            statusLower.includes("do not review") ||
            statusLower.includes("complete by client");

        const fileNumCheck = String(updatedFields.file_number || updatedFields.loan_number || modalFile.loan_number || modalFile.file_number || "").trim();
        if (!fileNumCheck || fileNumCheck === "-") {
            showToast("Cannot save! File Number is a mandatory field and cannot be left blank.", "error");
            return;
        }

        if (!bypassValidation && (statusLower === "completed" || statusLower === "rejected")) {
            const fileNum = fileNumCheck;
            const dateCol = String(updatedFields.date_column || modalFile.date_column || "").trim();
            const clientVal = String(updatedFields.client_name || modalFile.client_name || "").trim();
            const addressVal = String(updatedFields.address || modalFile.address || "").trim();
            const assignedVal = String(updatedFields.assigned_to || modalFile.assigned_to || "").trim();
            const inTimeStr = String(updatedFields.in_time || modalFile.in_time || "").trim();
            const assignTimeStr = String(updatedFields.assign_time || modalFile.assign_time || "").trim();
            const outTimeStr = String(updatedFields.out_time || modalFile.out_time || "").trim();

            const missing = [];
            if (!fileNum || fileNum === "-") missing.push("File Number");
            if (!dateCol || dateCol === "-") missing.push("Date");
            if (!inTimeStr || inTimeStr === "-") missing.push("In Time");
            if (!assignTimeStr || assignTimeStr === "-") missing.push("Assign Time");

            if (!isManager) {
                if (!clientVal || clientVal === "-") missing.push("Client");
                if (!addressVal || addressVal === "-") missing.push("Address");
                if (!assignedVal || assignedVal === "-") missing.push("Assigned To");
                if (!outTimeStr || outTimeStr === "-") missing.push("Out Time");
            }

            if (missing.length > 0) {
                showToast(`Cannot change status! The following mandatory fields are required: ${missing.join(", ")}`, "error");
                return;
            }

            if (inTimeStr && outTimeStr && inTimeStr !== "-" && outTimeStr !== "-") {
                const start = parseCustomDate(inTimeStr);
                const end = parseCustomDate(outTimeStr);

                if (!start || !end) {
                    showToast("Cannot change status! InTime or OutTime has an invalid date/time format. Please correct it (e.g. DD-MM-YYYY HH:MM).", "error");
                    return;
                }

                if (end < start) {
                    showToast("Cannot change status! The entered sequence contains a calculation error. Please verify that OutTime is later than InTime.", "error");
                    return;
                }
            } else if (!isManager) {
                const start = parseCustomDate(inTimeStr);
                const end = parseCustomDate(outTimeStr);
                if (!start || !end || end < start) {
                    showToast("Cannot change status! Valid InTime and OutTime are required.", "error");
                    return;
                }
            }
        }

        try {
            const token = localStorage.getItem("token");
            const payload = { ...updatedFields };

            // Auto-update status based on assigned_to field
            if (payload.assigned_to && payload.assigned_to.trim() !== "" && (payload.status === "New" || !payload.status)) {
                payload.status = "Assigned";
            }
            if ((!payload.assigned_to || payload.assigned_to.trim() === "") && payload.status === "Assigned") {
                payload.status = "New";
            }

            if (payload.in_time) payload.shift = calculateShift(payload.in_time);
            payload.tat = calculateTAT(payload.in_time, payload.out_time);

            payload.sla_status = calculateSLA(
                payload.tat,
                payload.client_name || payload.activeClient,
                payload.department || payload.activeDept,
                payload.priority,
                clientTatMap
            );

            if (payload.date_column) {
                payload.day = getDayName(payload.date_column);
            }

            if (payload.pp || payload.market_value) {
                payload.ten_percent = calculateValueTenPercent(payload.pp, payload.market_value);
            }

            await axios.put(`/api/update-record/${targetId}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setFiles(prev => prev.map(f => {
                if ((f.id || f._id) === targetId) {
                    return { ...f, ...payload };
                }
                return f;
            }));

            setShowUpdateModal(false);
            setModalFile(null);
            showToast("File updated successfully!");
        } catch (err) {
            console.error("Failed to save modal update:", err);
            showToast("Failed to save changes. Please try again.", "error");
        }
    };

    const handleTableEdit = async (index, key, value, shouldSync = false) => {
        const recordToUpdate = paginatedFiles[index];
        if (!recordToUpdate) return;

        const targetId = recordToUpdate.id || recordToUpdate._id;

        const revertSingleRecord = async (fileId) => {
            try {
                const token = localStorage.getItem("token");
                const res = await fetch(`/api/files/single/${fileId}`, {
                    headers: { "Authorization": `Bearer ${token}` }
                });
                if (res.ok) {
                    const originalData = await res.json();
                    setFiles(prev => prev.map(f => (f.id || f._id) === fileId ? originalData : f));
                }
            } catch (err) {
                console.error("Failed to revert single record:", err);
            }
        };

        const canEdit = isManager || (recordToUpdate.assigned_to && recordToUpdate.assigned_to === currentUser.name);
        if (!canEdit) {
            alert("You cannot edit this file.");
            return;
        }

        if (key === "assigned_to" && !isManager) {
            alert("Only the Manager can re-assign files.");
            return;
        }

        // Auto-correct date/time strings if they contain typos or different formats
        if (key === "in_time" || key === "assign_time" || key === "out_time") {
            const parsed = parseCustomDate(value);
            if (parsed) {
                value = formatDateTime(parsed);
            }
        }

        // Block clearing mandatory File Number inline
        if (shouldSync && (key === "file_number" || key === "loan_number")) {
            const valStr = String(value || "").trim();
            if (!valStr || valStr === "-") {
                alert("❌ File Number is a mandatory field and cannot be left blank.");
                revertSingleRecord(targetId);
                return;
            }
        }

        // Block clearing mandatory fields on Completed or Rejected files inline
        const statusLowerExisting = String(recordToUpdate.status || "").trim().toLowerCase();
        const adminMandatoryKeys = ["file_number", "loan_number", "date_column", "in_time", "assign_time"];
        const fullMandatoryKeys = ["file_number", "loan_number", "date_column", "client_name", "address", "assigned_to", "in_time", "assign_time", "out_time"];
        const mandatoryFieldKeys = isManager ? adminMandatoryKeys : fullMandatoryKeys;
        if (shouldSync && (statusLowerExisting === "completed" || statusLowerExisting === "rejected") && mandatoryFieldKeys.includes(key)) {
            const valStr = String(value || "").trim();
            if (!valStr || valStr === "-") {
                const fieldNames = {
                    file_number: "File Number",
                    loan_number: "File Number",
                    date_column: "Date",
                    client_name: "Client",
                    address: "Address",
                    assigned_to: "Assigned To",
                    in_time: "In Time",
                    assign_time: "Assign Time",
                    out_time: "Out Time"
                };
                alert(`❌ Cannot clear ${fieldNames[key] || key} on a completed file. All mandatory fields are required.`);
                revertSingleRecord(targetId);
                return;
            }
        }

        if (shouldSync && (key === "status" || key === "substatus")) {
            let targetStatus = recordToUpdate.status;
            if (key === "status") {
                targetStatus = value;
            } else if (key === "substatus") {
                const lowerVal = String(value || "").trim().toLowerCase();
                if (lowerVal === "wrong report" || lowerVal === "do not review" || lowerVal === "complete by client") {
                    targetStatus = value;
                } else {
                    targetStatus = getStatusFromSubstatus(value) || recordToUpdate.status;
                }
            }

            const statusLower = String(targetStatus || "").trim().toLowerCase();
            const bypassValidation =
                statusLower === "working" ||
                statusLower === "wrong report" ||
                statusLower === "do not review" ||
                statusLower === "complete by client" ||
                statusLower.includes("wrong report") ||
                statusLower.includes("do not review") ||
                statusLower.includes("complete by client");

            if (!bypassValidation && (statusLower === "completed" || statusLower === "rejected")) {
                const fileNum = String(recordToUpdate.file_number || recordToUpdate.loan_number || "").trim();
                const dateCol = String(recordToUpdate.date_column || "").trim();
                const clientVal = String(recordToUpdate.client_name || recordToUpdate.activeClient || "").trim();
                const addressVal = String(recordToUpdate.address || "").trim();
                const assignedVal = String(recordToUpdate.assigned_to || "").trim();
                const inTimeStr = String(recordToUpdate.in_time || "").trim();
                const assignTimeStr = String(recordToUpdate.assign_time || "").trim();
                const outTimeStr = String(recordToUpdate.out_time || "").trim();

                const missing = [];
                if (!fileNum || fileNum === "-") missing.push("File Number");
                if (!dateCol || dateCol === "-") missing.push("Date");
                if (!inTimeStr || inTimeStr === "-") missing.push("In Time");
                if (!assignTimeStr || assignTimeStr === "-") missing.push("Assign Time");

                if (!isManager) {
                    if (!clientVal || clientVal === "-") missing.push("Client");
                    if (!addressVal || addressVal === "-") missing.push("Address");
                    if (!assignedVal || assignedVal === "-") missing.push("Assigned To");
                    if (!outTimeStr || outTimeStr === "-") missing.push("Out Time");
                }

                if (missing.length > 0) {
                    alert(`❌ Cannot change status! The following mandatory fields are required: ${missing.join(", ")}`);
                    revertSingleRecord(targetId);
                    return;
                }

                if (inTimeStr && outTimeStr && inTimeStr !== "-" && outTimeStr !== "-") {
                    const start = parseCustomDate(inTimeStr);
                    const end = parseCustomDate(outTimeStr);

                    if (!start || !end) {
                        alert("❌ Cannot change status! InTime or OutTime has an invalid date/time format. Please correct it (e.g. DD-MM-YYYY HH:MM).");
                        revertSingleRecord(targetId);
                        return;
                    }

                    if (end < start) {
                        alert("❌ Calculation error: OutTime cannot be earlier than InTime.");
                        revertSingleRecord(targetId);
                        return;
                    }
                } else if (!isManager) {
                    const start = parseCustomDate(inTimeStr);
                    const end = parseCustomDate(outTimeStr);
                    if (!start || !end || end < start) {
                        alert("❌ Cannot change status! Valid InTime and OutTime are required.");
                        revertSingleRecord(targetId);
                        return;
                    }
                }
            }
        }

        const actualIndex = files.findIndex(f => (f.id || f._id) === targetId);
        if (actualIndex === -1) return;

        const updatedFiles = [...files];
        const updatedRecord = { ...updatedFiles[actualIndex], [key]: value };

        if (key === "file_number" || key === "loan_number") {
            updatedRecord.file_number = value;
            updatedRecord.loan_number = value;
        }

        if (key === "notes" || key === "notes_message") {
            updatedRecord.notes = value;
            updatedRecord.notes_message = value;
        }

        if (key === "reviewer_name" || key === "Reviewer_Name") {
            updatedRecord.reviewer_name = value;
            updatedRecord.Reviewer_Name = value;
        }

        if (key === "substatus") {
            const lowerVal = String(value || "").trim().toLowerCase();

            if (lowerVal === "wrong report" || lowerVal === "do not review" || lowerVal === "complete by client") {
                updatedRecord.status = value;
            } else {
                const mappedStatus = getStatusFromSubstatus(value);
                if (mappedStatus) updatedRecord.status = mappedStatus;
            }
        }

        if (key === "assigned_to" || key === "employee" || key === "Employee") {
            const assignedName = String(value || "").trim();

            updatedRecord.assigned_to = assignedName;
            updatedRecord.assignedTo = assignedName;
            updatedRecord.Reviewer_Name = assignedName;
            updatedRecord.reviewer_name = assignedName;
            updatedRecord.Employee = assignedName;
            updatedRecord.employee = assignedName;
            updatedRecord.status = assignedName !== "" ? "Assigned" : "New";
        }
        if (key === "in_time" || key === "out_time" || key === "priority") {
            if (key === "in_time") updatedRecord.shift = calculateShift(value);
            updatedRecord.tat = calculateTAT(updatedRecord.in_time, updatedRecord.out_time);

            updatedRecord.sla_status = calculateSLA(
                updatedRecord.tat,
                updatedRecord.client_name || updatedRecord.activeClient,
                updatedRecord.department || updatedRecord.activeDept,
                updatedRecord.priority,
                clientTatMap
            );
        }

        if (key === "date_column") {
            updatedRecord.day = getDayName(value);
        }

        if (key === "pp" || key === "market_value") {
            updatedRecord.ten_percent = calculateValueTenPercent(updatedRecord.pp, updatedRecord.market_value);
        }

        updatedFiles[actualIndex] = updatedRecord;
        setFiles(updatedFiles);

        if (shouldSync && targetId) {
            try {
                const token = localStorage.getItem("token");
                if (key === "assigned_to" || key === "employee" || key === "Employee") {
                    const assignedName = String(value || "").trim();

                    await axios.put("/api/files/bulk-assign", {
                        fileIds: [targetId],
                        assignedTo: assignedName,
                        assigned_to: assignedName,
                        status: assignedName !== "" ? "Assigned" : "New",
                        lastMovedAt: new Date().toISOString()
                    }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (typeof fetchData === "function") {
                        fetchData(false);
                    }

                    return;
                }

                const dbRecord = getDbUpdatePayload(key, value, updatedRecord);
                await axios.put(`/api/update-record/${targetId}`, dbRecord, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (err) {
                console.error("Failed to update record:", err);
                alert("Failed to save changes. Please check backend console.");
            }
        }
    };
    const handleDeleteClick = (globalIndex) => {
        if (!isManager) {
            showToast("Only Admin and Account Manager can delete records.", "error");
            return;
        }
        setDeleteIndex(globalIndex);
        setShowDeleteModal(true);
    };

    const confirmDeleteRecord = async () => {
        if (deleteIndex === null) return;

        const recordToDelete = files[deleteIndex];
        if (!recordToDelete) return;

        const targetId = recordToDelete.id || recordToDelete._id;

        try {
            if (targetId) {
                const token = localStorage.getItem("token");
                await axios.delete(`/api/files/delete/${targetId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            setFiles(prev => prev.filter(f => (f.id || f._id) !== targetId));
            setSelectedFiles(prev => prev.filter(id => id !== targetId));
            setShowDeleteModal(false);
            setDeleteIndex(null);
            showToast("Record deleted successfully!");
        } catch (err) {
            console.error("Delete Error:", err);
            showToast("Error deleting record.", "error");
        }
    };

    const filteredFiles = useMemo(() => {
        const searchLower = searchTerm.toLowerCase().trim();

        let list = files;
        if (showDuplicatesOnly) {
            list = list.filter(file => {
                const num = String(file.file_number || file.loan_number || "").trim().toLowerCase();
                return duplicateFileNumbers.has(num);
            });
        }

        if (searchLower !== "") {
            return list.filter(file => {
                if (!isManager && file.assigned_to !== currentUser.name) return false;

                return (
                    String(file.file_number || "").toLowerCase().includes(searchLower) ||
                    String(file.address || "").toLowerCase().includes(searchLower) ||
                    String(file.assigned_to || "").toLowerCase().includes(searchLower)
                );
            });
        }

        return list.filter(file => {
            const statusVal = String(file.status || "").trim().toLowerCase();
            const substatus = String(file.substatus || "").trim().toLowerCase();

            const isException =
                statusVal === "wrong report" || statusVal === "do not review" || statusVal === "complete by client" ||
                substatus === "wrong report" || substatus === "do not review" || substatus === "complete by client";

            if (!isManager && file.assigned_to !== currentUser.name) return false;

            if (activeStatus === "COMPLETED") {
                if (statusVal !== "completed" && statusVal !== "rejected") return false;
            } else if (activeStatus === "ASSIGNED") {
                if ((statusVal !== "assigned" && statusVal !== "working" && statusVal !== "lender revision") || statusVal === "completed" || statusVal === "rejected" || isException) return false;
            } else if (activeStatus === "EXCEPTIONS") {
                if (!isException) return false;
            } else {
                if (statusVal === "completed" || statusVal === "rejected" || isException) return false;
            }

            const priority = String(file.priority || "").toLowerCase().trim();

            if (subFilter === "New") return (!file.assigned_to || String(file.assigned_to).trim() === "") && statusVal !== "completed" && statusVal !== "rejected" && !isException;
            if (subFilter === "AssignedOnly") return file.assigned_to && String(file.assigned_to).trim() !== "" && statusVal !== "completed" && statusVal !== "rejected" && !isException;

            if (subFilter === "VIP") return priority === "vip";
            if (subFilter === "Rush") return priority.includes("rush");
            if (subFilter === "Due") return priority.includes("due date") && !priority.includes("past");
            if (subFilter === "Past Due") return priority.includes("past due");
            if (subFilter === "Late") return priority.includes("late");

            return true;
        });
    }, [files, activeStatus, subFilter, searchTerm, isManager, currentUser.name, showDuplicatesOnly, duplicateFileNumbers]);

    const totalPages = Math.ceil(filteredFiles.length / itemsPerPage);
    const paginatedFiles = filteredFiles.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const toggleSelectAll = (e) => {
        if (!isManager) return;

        if (e.target.checked) {
            setSelectedFiles(prev => [...new Set([...prev, ...paginatedFiles.map(f => f.id || f._id)])]);
        } else {
            const pageIds = paginatedFiles.map(f => f.id || f._id);
            setSelectedFiles(prev => prev.filter(id => !pageIds.includes(id)));
        }
    };

    const toggleFileSelection = (id) => {
        if (!isManager) return;
        setSelectedFiles(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    };

    const handleBulkAssign = async () => {
        if (!isManager) {
            showToast("Only the Manager can assign files.", "error");
            return;
        }

        if (!bulkEmployee || selectedFiles.length === 0) {
            showToast("Please select files and an employee first.", "error");
            return;
        }

        try {
            const token = localStorage.getItem("token");
            await axios.put("/api/files/bulk-assign", {
                fileIds: selectedFiles,
                assignedTo: bulkEmployee,
                assigned_to: bulkEmployee,
                status: "Assigned",
                lastMovedAt: new Date().toISOString()
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setFiles(prevFiles => prevFiles.map(file => {
                if (selectedFiles.includes(file.id || file._id)) {
                    return {
                        ...file,
                        assigned_to: bulkEmployee,
                        assignedTo: bulkEmployee,
                        Reviewer_Name: bulkEmployee,
                        reviewer_name: bulkEmployee,
                        Employee: bulkEmployee,
                        employee: bulkEmployee,
                        status: "Assigned"
                    };
                }

                return file;
            }));

            showToast(`Assigned ${selectedFiles.length} files to ${bulkEmployee} successfully!`);
            setSelectedFiles([]);
            setBulkEmployee("");

            if (typeof fetchData === "function") {
                fetchData(false);
            }
        } catch (err) {
            showToast("Error saving bulk assignment.", "error");
        }
    };

    const handleBulkDelete = () => {
        if (!isManager) {
            showToast("Only the Admin and Account Manager can delete files.", "error");
            return;
        }

        if (selectedFiles.length === 0) {
            showToast("Please select files first.", "error");
            return;
        }

        setShowBulkDeleteModal(true);
    };

    const confirmBulkDelete = async () => {
        try {
            const token = localStorage.getItem("token");
            await Promise.all(selectedFiles.map(id => axios.delete(`/api/files/delete/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            })));
            setFiles(prevFiles => prevFiles.filter(file => !selectedFiles.includes(file.id || file._id)));
            setShowBulkDeleteModal(false);
            setSelectedFiles([]);
            showToast("Selected files deleted successfully!");
        } catch (err) {
            showToast("Error deleting files.", "error");
        }
    };

    const handleExport = () => {
        if (filteredFiles.length === 0) {
            showToast("No data to export.", "error");
            return;
        }

        const isQc = activeDept === "QC" || String(activeDept).toLowerCase().includes("qc");
        const forbiddenColumns = [
            "_id", "id", "__v",
            "activeDept", "activeClient", "department",
            "locked_by", "last_moved_at", "is_duplicate",
            "assigned_by", "substatus",
            "pst_in", "inflow_pst", "time_in",
            "pst_out", "outflow_pst", "time_out",
            "received_date",
            "client_id", "clientId",
            "comments",
            "sla_start_time", "sla_end_time"
        ];

        const exportCols = currentColumns.filter(k => !forbiddenColumns.includes(k));

        const dataToExport = filteredFiles.map((file, index) => {
            const row = {};

            exportCols.forEach(col => {
                if (String(col).toLowerCase() === "sr_no") {
                    row["SR NO"] = index + 1;
                } else {
                    let header = col.replace(/_/g, " ").toUpperCase();
                    if (col === "date_column") header = "DATE";
                    if (col === "pp") header = "PURCHASE PRICE";
                    if (col === "tat") header = "SLA TAT";
                    if (col === "reviewer_tat") header = "REVIEWER TAT";
                    let val = col === "reviewer_tat"
                        ? (calculateTAT(file.assign_time, file.out_time) || "")
                        : (file[col] ?? "");

                    if (val === 0 || val === "0" || val === "0.0" || val === "0.00") {
                        val = "";
                    }



                    if (col === "date_column") {
                        if (val) {
                            const cleanStr = String(val).trim().split("T")[0].split(" ")[0].replace(/\//g, "-");
                            if (/^\d{4}-\d{2}-\d{2}$/.test(cleanStr)) {
                                const [y, m, d] = cleanStr.split("-");
                                val = `${d.padStart(2, "0")}-${m.padStart(2, "0")}-${y}`;
                            } else if (/^\d{2}-\d{2}-\d{4}$/.test(cleanStr)) {
                                val = cleanStr;
                            } else {
                                const dObj = new Date(val);
                                if (!isNaN(dObj.getTime())) {
                                    const day = String(dObj.getUTCDate()).padStart(2, "0");
                                    const month = String(dObj.getUTCMonth() + 1).padStart(2, "0");
                                    const year = dObj.getUTCFullYear();
                                    val = `${day}-${month}-${year}`;
                                }
                            }
                        }
                    } else if (col === "in_time" || col === "assign_time" || col === "out_time") {
                        if (val) {
                            const d = new Date(val);

                            if (!isNaN(d.getTime()) && d.getFullYear() > 1900) {
                                const day = String(d.getDate()).padStart(2, "0");
                                const month = String(d.getMonth() + 1).padStart(2, "0");
                                const year = d.getFullYear();
                                const hours = String(d.getHours()).padStart(2, "0");
                                const mins = String(d.getMinutes()).padStart(2, "0");

                                val = `${day}-${month}-${year} ${hours}:${mins}`;
                            } else {
                                val = "";
                            }
                        }
                    }

                    row[header] = val;
                }
            });

            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Production_Report");
        XLSX.writeFile(workbook, `${activeClient || "Export"}_${new Date().toLocaleDateString()}.xlsx`);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();

        const cleanHeader = (value) =>
            String(value ?? "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");

        const isEmpty = (value) =>
            value === null || value === undefined || String(value).trim() === "";

        const normalize = (key, val) => {
            if (val === null || val === undefined || val === "") return "";

            const strVal = val instanceof Date ? val.toISOString() : String(val).trim();

            if (DROPDOWN_FIELDS[key]) {
                const match = DROPDOWN_FIELDS[key].find(
                    opt => String(opt).trim().toLowerCase() === strVal.toLowerCase()
                );

                return match || strVal;
            }

            return strVal;
        };

        const toMysqlDate = (value) => {
            if (!value) return "";

            if (typeof value === "number") {
                const parsed = XLSX.SSF.parse_date_code(value);
                if (!parsed) return "";
                return `${parsed.y}-${String(parsed.m).padStart(2, "0")}-${String(parsed.d).padStart(2, "0")}`;
            }

            const parsed = parseCustomDate(value);
            if (parsed && !isNaN(parsed.getTime())) {
                return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
            }

            const d = value instanceof Date ? value : new Date(value);
            if (isNaN(d.getTime())) return "";

            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        };

        const buildHeaderMap = (headers) => {
            const map = {};

            headers.forEach((header, index) => {
                const h = cleanHeader(header);
                if (!h) return;

                if (h === "srno" || h === "sr") map.sr_no = index;
                else if (h === "date" || h === "datecolumn") map.date_column = index;
                else if (h === "priority") map.priority = index;
                else if (
                    h === "filenumber" ||
                    h === "ordernumber" ||
                    h === "ordernumberrevised" ||
                    h === "loannumber" ||
                    h === "revisedfilenumber" ||
                    h === "fileno" ||
                    h === "orderno" ||
                    h === "loanno" ||
                    h.includes("filenumber") ||
                    h.includes("ordernumber") ||
                    h.includes("loannumber")
                ) map.file_number = index;
                else if (h === "address" || h === "propertyaddress") map.address = index;
                else if (h === "formtype" || h === "productcode") map.product_code = index;
                else if (h === "appraisername") map.appraiser_name = index;
                else if (h === "loantype") map.loan_type = index;
                else if (h === "lendername" || h === "lender") map.lender_name = index;
                else if (h === "lowerthancontract") map.lower_than_contract = index;
                else if (h === "crosscheckresults") map.crosscheck_results = index;
                else if (h === "intime") map.in_time = index;
                else if (h === "assigntime" || h === "assignedtime" || h === "assigntimestamp") map.assign_time = index;
                else if (h === "outtime") map.out_time = index;
                else if (h === "pp" || h === "purchaseprice" || h === "purchase_price") map.pp = index;
                else if (h === "marketvalue") map.market_value = index;
                else if (h === "assignedto" || h === "assignee" || h === "empname") map.assigned_to = index;
                else if (h === "tat") map.tat = index;
                else if (h === "reviewer" || h === "reviewername") map.reviewer_name = index;
                else if (h === "qcby") map.qc_by = index;
                else if (h === "feedback") map.feedback = index;
                else if (h === "training") map.training = index;
                else if (h === "trainingfindings") map.training_findings = index;
                else if (h === "auditorfindings") map.auditor_findings = index;
                else if (h === "feedback2") map.feedback2 = index;
                else if (h === "status") map.status = index;
                else if (h === "substatus") map.substatus = index;
                else if (h === "productionlog") map.production_log = index;
                else if (h === "escalations") map.escalations = index;
                else if (h === "doubts") map.doubts = index;
                else if (h === "advisor" || h === "advisornote") map.advisor_note = index;
                else if (h === "notes" || h === "notesmessage" || h === "reason") map.notes_message = index;
                else if (h === "revisions" || h === "rivisions") map.revisions = index;
                else if (h === "sla" || h === "slastatus") map.sla_status = index;
                else if (h === "shift") map.shift = index;
                else if (h === "day") map.day = index;
            });

            return map;
        };

        const looksLikeHeaderRow = (row) => {
            const headerMap = buildHeaderMap(row);
            return headerMap.file_number !== undefined && Object.keys(headerMap).length >= 1;
        };

        const getCell = (row, map, key) => {
            const index = map[key];
            if (index === undefined) return "";
            return row[index] ?? "";
        };

        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: "binary", cellDates: true });

                const formattedData = [];
                const seenKeys = new Set();

                // 1. Identify hidden sheets from workbook metadata (Hidden === 1 or Hidden === 2)
                const hiddenSheetSet = new Set();
                if (wb.Workbook && Array.isArray(wb.Workbook.Sheets)) {
                    wb.Workbook.Sheets.forEach(s => {
                        if (s && (s.Hidden === 1 || s.Hidden === 2 || s.Hidden === true)) {
                            hiddenSheetSet.add(s.name);
                        }
                    });
                }

                // 2. Filter out hidden sheets so hidden tabs are never imported
                const visibleSheetNames = wb.SheetNames.filter(name => !hiddenSheetSet.has(name));
                const candidateSheets = visibleSheetNames.length > 0 ? visibleSheetNames : wb.SheetNames;

                // 3. Smart sheet selection and department routing among visible sheets
                const hasExplicitDeptSheet = candidateSheets.some(name => {
                    const clean = String(name || "").trim().toLowerCase();
                    return clean.includes("qc") || clean.includes("revised") || clean.includes("report");
                });

                // If explicit department sheets exist (e.g. "QC" and "Revised"), process each matching sheet.
                // If only generic sheets exist (e.g. Sheet1, Sheet2), only process the first sheet
                // so a duplicate/backup sheet never doubles up files into the same department!
                const sheetsToProcess = hasExplicitDeptSheet
                    ? candidateSheets.filter(name => {
                        const clean = String(name || "").trim().toLowerCase();
                        return clean.includes("qc") || clean.includes("revised") || clean.includes("report");
                    })
                    : [candidateSheets[0]];

                sheetsToProcess.forEach((sheetName) => {
                    const ws = wb.Sheets[sheetName];
                    const rows = XLSX.utils.sheet_to_json(ws, {
                        header: 1,
                        defval: "",
                        raw: false
                    });

                    // Determine department for this sheet:
                    // If multiple sheets are being imported (e.g. workbook has both QC and Revised sheets visible),
                    // route each sheet according to its tab name.
                    // If only 1 sheet is being imported (or other tabs are hidden), respect the current page (activeDept).
                    let targetDept = activeDept;
                    if (sheetsToProcess.length > 1) {
                        const cleanSheet = String(sheetName || "").trim().toLowerCase();
                        if (cleanSheet.includes("revised") || cleanSheet.includes("report")) {
                            targetDept = "Revised";
                        } else if (cleanSheet.includes("qc")) {
                            targetDept = "QC";
                        }
                    }

                    let currentHeaderMap = null;

                    rows.forEach((row) => {
                        if (looksLikeHeaderRow(row)) {
                            currentHeaderMap = buildHeaderMap(row);
                            return;
                        }

                        if (!currentHeaderMap) return;

                        const fileNumber = getCell(row, currentHeaderMap, "file_number");
                        if (isEmpty(fileNumber)) return;

                        const rawDate = getCell(row, currentHeaderMap, "date_column");
                        const rawIn = getCell(row, currentHeaderMap, "in_time");
                        const rawAssign = getCell(row, currentHeaderMap, "assign_time");
                        const rawOut = getCell(row, currentHeaderMap, "out_time");
                        const tatValue = calculateTAT(rawIn, rawOut);

                        const statusFromSheet =
                            String(getCell(row, currentHeaderMap, "status") || getCell(row, currentHeaderMap, "substatus") || "").trim();

                        const lowerVal = statusFromSheet.toLowerCase();

                        let autoStatus = "New";
                        if (lowerVal.includes("completed") || lowerVal.includes("complete")) autoStatus = "Completed";
                        else if (lowerVal.includes("rejected") || lowerVal.includes("reject")) autoStatus = "Rejected";
                        else if (lowerVal.includes("wrong report")) autoStatus = "Wrong Report";
                        else if (lowerVal.includes("do not review")) autoStatus = "Do Not Review";
                        else if (lowerVal.includes("complete by client")) autoStatus = "Complete by Client";
                        else if (lowerVal.includes("working") || lowerVal.includes("progress") || lowerVal.includes("pending")) autoStatus = "Working";
                        else if (lowerVal.includes("assigned")) autoStatus = "Assigned";

                        const pp = getCell(row, currentHeaderMap, "pp");
                        const marketValue = getCell(row, currentHeaderMap, "market_value");

                        const targetClient = activeClient === "MMA" ? "ANM" : activeClient;
                        const priorityValue = normalize("priority", getCell(row, currentHeaderMap, "priority"));

                        formattedData.push({
                            ...INITIAL_STATE,

                            sr_no: getCell(row, currentHeaderMap, "sr_no"),
                            date_column: toMysqlDate(rawDate),

                            file_number: normalize("file_number", fileNumber),
                            loan_number: normalize("loan_number", fileNumber),

                            priority: priorityValue,
                            address: getCell(row, currentHeaderMap, "address"),
                            lender_name: getCell(row, currentHeaderMap, "lender_name"),
                            product_code: getCell(row, currentHeaderMap, "product_code"),
                            appraiser_name: getCell(row, currentHeaderMap, "appraiser_name"),
                            loan_type: getCell(row, currentHeaderMap, "loan_type"),
                            lower_than_contract: getCell(row, currentHeaderMap, "lower_than_contract"),
                            crosscheck_results: getCell(row, currentHeaderMap, "crosscheck_results"),

                            in_time: rawIn,
                            assign_time: rawAssign,
                            out_time: rawOut,
                            tat: tatValue || getCell(row, currentHeaderMap, "tat"),

                            sla_status: calculateSLA(tatValue, targetClient, targetDept, priorityValue, clientTatMap) || normalize("sla_status", getCell(row, currentHeaderMap, "sla_status")),
                            shift: calculateShift(rawIn) || normalize("shift", getCell(row, currentHeaderMap, "shift")),
                            day: (getDayName ? getDayName(rawIn) : "") || getCell(row, currentHeaderMap, "day"),

                            pp,
                            market_value: marketValue,
                            ten_percent: calculateValueTenPercent(pp, marketValue),

                            assigned_to: normalize("assigned_to", getCell(row, currentHeaderMap, "assigned_to")),
                            reviewer_name: getCell(row, currentHeaderMap, "reviewer_name"),

                            qc_by: getCell(row, currentHeaderMap, "qc_by"),
                            feedback: getCell(row, currentHeaderMap, "feedback"),
                            training: getCell(row, currentHeaderMap, "training"),
                            training_findings: getCell(row, currentHeaderMap, "training_findings"),
                            auditor_findings: getCell(row, currentHeaderMap, "auditor_findings"),
                            feedback2: getCell(row, currentHeaderMap, "feedback2"),

                            notes: getCell(row, currentHeaderMap, "notes_message"),
                            notes_message: getCell(row, currentHeaderMap, "notes_message"),

                            production_log: normalize("production_log", getCell(row, currentHeaderMap, "production_log")),
                            escalations: getCell(row, currentHeaderMap, "escalations"),
                            doubts: getCell(row, currentHeaderMap, "doubts"),
                            advisor_note: getCell(row, currentHeaderMap, "advisor_note"),
                            revisions: getCell(row, currentHeaderMap, "revisions"),

                            status: normalize("status", autoStatus),
                            substatus: normalize("substatus", statusFromSheet),

                            activeDept: targetDept,
                            activeClient: targetClient,
                            department: targetDept,
                            client_name: targetClient
                        });
                    });
                });

                if (formattedData.length === 0) {
                    showToast("No valid records found. FileNumber / OrderNumber column was not found.", "error");
                    return;
                }

                setIsImporting(true);
                setImportProgress(0);

                const chunkSize = 100;
                let importedCount = 0;

                const token = localStorage.getItem("token");
                for (let i = 0; i < formattedData.length; i += chunkSize) {
                    const chunk = formattedData.slice(i, i + chunkSize);
                    await axios.post("/api/bulk-import", { data: chunk }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    importedCount += chunk.length;
                    const progress = Math.min(100, Math.round((importedCount / formattedData.length) * 100));
                    setImportProgress(progress);
                }

                showToast(`Imported ${importedCount} records successfully!`);
                fetchData();
            } catch (err) {
                console.error("Import failed:", err);
                showToast("Import failed. Please check console/backend.", "error");
            } finally {
                setIsImporting(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            }
        };

        reader.readAsBinaryString(file);
    };

    const renderCell = (f, key, i, canEdit) => {
        let val = key === "reviewer_tat"
            ? (calculateTAT(f.assign_time, f.out_time) || "")
            : (f[key] ?? "");

        // Make only Assigned To column editable directly in the grid.
        // All other columns will fall back to read-only views.
        const isCellEditable = canEdit && (key === "assigned_to" || key === "assignedto" || key === "employee");

        if (f.in_time && f.in_time !== "-") {
            const statusVal = String(f.status || "").trim().toLowerCase();
            const substatus = String(f.substatus || "").trim().toLowerCase();

            const hasOutTime = f.out_time && f.out_time !== "" && f.out_time !== "-";
            const isClosedOrException =
                hasOutTime ||
                statusVal === "completed" || statusVal === "rejected" ||
                statusVal === "wrong report" || statusVal === "do not review" || statusVal === "complete by client" ||
                substatus === "wrong report" || substatus === "do not review" || substatus === "complete by client";

            if (!isClosedOrException) {
                console.log("FileTable Active SLA Debug:", {
                    id: f.id,
                    loan_number: f.loan_number,
                    in_time: f.in_time,
                    parsed_start: parseCustomDate(f.in_time)?.toString(),
                    current_time: new Date().toString()
                });
                const active = calculateActiveSLA(
                    f.in_time,
                    f.client_name || f.activeClient || "",
                    f.department || f.activeDept || "",
                    f.priority || "",
                    clientTatMap
                );
                if (key === "tat") {
                    val = active.tat;
                } else if (key === "sla_status") {
                    val = active.status;
                }
            }
        }

        if (val === 0 || val === "0" || val === "0.0" || val === "0.00") {
            val = "";
        }

        if (String(key).toLowerCase() === "file_number" && !val) {
            val = f.loan_number || "";
        }

        const inputStyle = {
            width: "max-content",
            height: "auto",
            minWidth: "140px",
            maxWidth: "500px",
            border: "none",
            background: "transparent",
            outline: "none",
            fontSize: "12px",
            padding: "8px",
            textAlign: "center",
            display: "inline-block",
            overflow: "hidden",
            boxSizing: "border-box",
            whiteSpace: "normal",
            wordBreak: "break-word"
        };

        const getBadgeColor = (columnKey, value) => {
            const cleanVal = String(value || "").trim().toLowerCase();

            if (columnKey === "priority") {
                if (cleanVal === "vip") return { bg: "#fff1f2", text: "#f01947" };
                if (cleanVal.includes("rush")) return { bg: "#fee2e2", text: "#b91c1c" };
                if (cleanVal.includes("late")) return { bg: "#ffedd5", text: "#9a3412" };
                if (cleanVal.includes("due")) return { bg: "#e0f2fe", text: "#0369a1" };
            }

            if (columnKey === "ten_percent" || columnKey === "sla_status") {
                if (cleanVal.includes("within")) return { bg: "#dcfce7", text: "#15803d" };
                if (cleanVal.includes("exceed") || cleanVal.includes("over")) return { bg: "#fee2e2", text: "#b91c1c" };
            }

            if (columnKey === "shift") {
                if (cleanVal === "live") return { bg: "#e0e7ff", text: "#4338ca" };
                if (cleanVal === "offline") return { bg: "#f3f4f6", text: "#374151" };
            }

            if (columnKey === "status") {
                if (cleanVal === "completed") return { bg: "#dcfce7", text: "#15803d" };
                if (cleanVal === "rejected") return { bg: "#fee2e2", text: "#b91c1c" };
                if (cleanVal === "working") return { bg: "#fef3c7", text: "#92400e" };
                if (cleanVal === "assigned") return { bg: "#e0f2fe", text: "#0369a1" };
                if (cleanVal === "wrong report" || cleanVal === "do not review" || cleanVal === "complete by client") return { bg: "#edf2f7", text: "#4a5568" };
            }

            return null;
        };

        const colors = getBadgeColor(key, val);

        const assignmentColumnKeys = ["assigned_to", "assignedto", "employee", "employee_name", "reviewer_name", "reviewer"];

        if (isCellEditable && assignmentColumnKeys.includes(String(key).toLowerCase())) {
            return (
                <div style={{ padding: "4px", display: "flex", justifyContent: "center" }}>
                    <select
                        value={val}
                        onChange={(e) => handleTableEdit(i, key, e.target.value, true)}
                        style={{
                            ...inputStyle,
                            width: "135px",
                            borderRadius: "8px",
                            border: "1px solid #ddd",
                            backgroundColor: colors ? colors.bg : "white",
                            color: colors ? colors.text : "black",
                            fontWeight: colors ? "bold" : "normal",
                            cursor: "pointer",
                            height: "32px"
                        }}
                    >
                        <option value="" style={{ background: "white", color: "black" }}>Select Employee</option>
                        {dynamicEmployees.map(nameOpt => (
                            <option key={nameOpt} value={nameOpt} style={{ background: "white", color: "black" }}>
                                {nameOpt}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }

        if (isCellEditable && DROPDOWN_FIELDS[key] && key !== "status") {
            return (
                <div style={{ padding: "4px", display: "flex", justifyContent: "center" }}>
                    <select
                        value={val}
                        onChange={(e) => handleTableEdit(i, key, e.target.value, true)}
                        style={{
                            ...inputStyle,
                            width: "135px",
                            borderRadius: "8px",
                            border: "1px solid #ddd",
                            backgroundColor: colors ? colors.bg : "white",
                            color: colors ? colors.text : "black",
                            fontWeight: colors ? "bold" : "normal",
                            cursor: "pointer",
                            height: "32px"
                        }}
                    >
                        <option value="" style={{ background: "white", color: "black" }}>Select</option>
                        {DROPDOWN_FIELDS[key].map(opt => (
                            <option key={opt} value={opt} style={{ background: "white", color: opt.toLowerCase() === "vip" ? "#e11d48" : "black", fontWeight: opt.toLowerCase() === "vip" ? "bold" : "normal" }}>
                                {opt}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }

        const badgeOnlyFields = ["ten_percent", "sla_status", "shift", "priority", "status"];

        if (badgeOnlyFields.includes(key)) {
            const isPriority = key === "priority";
            return (
                <div style={{
                    padding: isPriority ? "0px" : "8px", // <-- Adjust outer padding here
                    margin: isPriority ? "2px" : "0px",   // <-- Adjust outer margin here
                    textAlign: "center"
                }}>
                    <span style={colors ? {
                        padding: isPriority ? "6px 12px" : "4px 10px", // <-- Adjust inner badge padding here
                        borderRadius: "6px",
                        fontWeight: "bold",
                        backgroundColor: colors.bg,
                        color: colors.text,
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                        display: "inline-block"
                    } : {}}>
                        {val || "-"}
                    </span>
                </div>
            );
        }

        if (key === "revisions") {
            return (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "6px" }}>
                    {val ? (
                        <button
                            type="button"
                            onClick={() => handleShowRevisionsModal(val, f.loan_number || f.file_number || f.id)}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "6px 12px",
                                borderRadius: "20px",
                                border: "1px solid #bae6fd",
                                backgroundColor: "#e0f2fe",
                                color: "#0369a1",
                                fontSize: "11px",
                                fontWeight: "700",
                                cursor: "pointer",
                                transition: "all 0.2s ease"
                            }}
                            className="revisions-badge-btn"
                            title="Click to view revisions"
                        >
                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "14px", height: "14px", borderRadius: "50%", backgroundColor: "#0284c7", color: "white", fontSize: "10px", fontWeight: "bold" }}>i</span>
                            <span>View</span>
                        </button>
                    ) : (
                        <span style={{ color: "#cbd5e1" }}>-</span>
                    )}
                </div>
            );
        }

        const isFileNumberCol = String(key).toLowerCase() === "file_number" || String(key).toLowerCase() === "loan_number";
        const isDup = isFileNumberCol && duplicateFileNumbers.has(String(f.file_number || f.loan_number || "").trim().toLowerCase());

        if (isDup) {
            let finalDisplayVal = val;
            if (typeof val === "string" && val.includes("T") && val.includes("Z")) {
                finalDisplayVal = formatDateOnly(val);
            }
            return (
                <div style={{ padding: "4px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <div 
                        onClick={() => {
                            setSearchTerm(String(finalDisplayVal).trim());
                            setCurrentPage(1);
                        }}
                        title="Click to view all duplicates"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#fef3c7",
                            border: "1px solid #fcd34d",
                            borderRadius: "6px",
                            padding: "6px 12px",
                            color: "#92400e",
                            fontWeight: "bold",
                            cursor: "pointer",
                            fontSize: "12px",
                            transition: "all 0.2s ease"
                        }}
                        className="duplicate-badge-cell"
                    >
                        {finalDisplayVal}
                    </div>
                </div>
            );
        }

        const systemFields = ["tat", "reviewer_tat", "day"];

        if (systemFields.includes(key) || !isCellEditable) {
            let content = val || "-";

            if (typeof content === "string" && content.includes("T") && content.includes("Z")) {
                content = formatDateOnly(content);
            }

            if ((key === "in_time" || key === "assign_time" || key === "out_time") && val) {
                content = formatDateTime(val);
            }

            return (
                <div style={{ ...inputStyle, color: "#666", padding: "10px" }}>
                    {content}
                </div>
            );
        }

        if (key === "date_column") {
            let displayVal = "";
            if (val) {
                const d = new Date(val);
                if (!isNaN(d.getTime())) {
                    displayVal = formatDateForInput(val);
                }
            }
            return (
                <input
                    type="date"
                    value={displayVal}
                    onChange={(e) => handleTableEdit(i, key, e.target.value)}
                    onBlur={(e) => handleTableEdit(i, key, e.target.value, true)}
                    style={{ ...inputStyle, cursor: "pointer", width: "160px" }}
                />
            );
        }

        if (key === "in_time" || key === "assign_time" || key === "out_time") {
            let displayVal = val || "";
            if (val && typeof val === "string") {
                const d = parseCustomDate(val);
                if (d) {
                    const day = String(d.getDate()).padStart(2, "0");
                    const month = String(d.getMonth() + 1).padStart(2, "0");
                    const year = d.getFullYear();
                    const hh = String(d.getHours()).padStart(2, "0");
                    const min = String(d.getMinutes()).padStart(2, "0");
                    displayVal = `${day}-${month}-${year} ${hh}:${min}`;
                }
            }
            return (
                <div style={{ display: "inline-flex", gap: "4px", width: "160px", alignItems: "center", position: "relative" }}>
                    <input
                        type="text"
                        value={displayVal}
                        placeholder="DD-MM-YYYY HH:MM (e.g., 25-06-2026 11:40)"
                        onChange={(e) => handleTableEdit(i, key, e.target.value)}
                        onBlur={(e) => handleTableEdit(i, key, e.target.value, true)}
                        style={{ ...inputStyle, cursor: "text", flex: 1, minWidth: 0, paddingRight: "24px" }}
                    />
                    <button
                        type="button"
                        onClick={(e) => {
                            const hiddenPicker = e.currentTarget.nextSibling;
                            if (hiddenPicker) {
                                try {
                                    hiddenPicker.showPicker();
                                } catch (err) {
                                    hiddenPicker.focus();
                                    hiddenPicker.click();
                                }
                            }
                        }}
                        style={{
                            position: "absolute",
                            right: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "12px",
                            padding: "2px",
                            color: "#64748b"
                        }}
                        title="Open Date-Time Picker"
                    >
                        📅
                    </button>
                    <input
                        type="datetime-local"
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val) {
                                const d = new Date(val);
                                if (!isNaN(d.getTime())) {
                                    const day = String(d.getDate()).padStart(2, "0");
                                    const month = String(d.getMonth() + 1).padStart(2, "0");
                                    const year = d.getFullYear();
                                    const hh = String(d.getHours()).padStart(2, "0");
                                    const min = String(d.getMinutes()).padStart(2, "0");
                                    const formatted = `${day}-${month}-${year} ${hh}:${min}`;
                                    handleTableEdit(i, key, formatted, true);
                                }
                            }
                        }}
                        style={{
                            position: "absolute",
                            width: 0,
                            height: 0,
                            opacity: 0,
                            pointerEvents: "none"
                        }}
                    />
                </div>
            );
        }

        let finalDisplayVal = val;

        if (typeof val === "string" && val.includes("T") && val.includes("Z")) {
            finalDisplayVal = formatDateOnly(val);
        }





        return (
            <textarea
                value={finalDisplayVal}
                rows={1}
                onChange={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                    handleTableEdit(i, key, e.target.value);
                }}
                onBlur={(e) => handleTableEdit(i, key, e.target.value, true)}
                style={{
                    ...inputStyle,
                    textAlign: "center",
                    backgroundColor: "transparent",
                    resize: "none",
                    color: "black",
                    fontWeight: "normal"
                }}
            />
        );
    };

    const fileNumberCounts = useMemo(() => {
        return files.reduce((acc, file) => {
            const ln = String(file.loan_number || "").trim();
            if (ln) acc[ln] = (acc[ln] || 0) + 1;
            return acc;
        }, {});
    }, [files]);

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", position: "relative" }}>
            <input type="file" ref={fileInputRef} style={{ display: "none" }} accept=".xlsx, .xls, .csv" onChange={handleFileUpload} />

            <div style={{ display: "flex", backgroundColor: "white", borderBottom: "1px solid #e2e8f0" }}>
                {["ALL OPEN", "ASSIGNED", "COMPLETED", "EXCEPTIONS"].map((tab) => {
                    const count = files.filter(f => {
                        const s = String(f.status || "").trim().toLowerCase();
                        const sub = String(f.substatus || "").trim().toLowerCase();

                        const isException =
                            s === "wrong report" || s === "do not review" || s === "complete by client" ||
                            sub === "wrong report" || sub === "do not review" || sub === "complete by client";

                        if (!isManager && f.assigned_to !== currentUser.name) return false;
                        if (tab === "EXCEPTIONS") return isException;
                        if (tab === "COMPLETED") return s === "completed" || s === "rejected";
                        if (tab === "ASSIGNED") return (s === "assigned" || s === "working" || s === "lender revision") && s !== "completed" && s !== "rejected" && !isException;

                        return s !== "completed" && s !== "rejected" && !isException;
                    }).length;

                    return (
                        <div key={tab} onClick={() => { setActiveStatus(tab); setSubFilter("Total"); }} style={{ flex: 1, padding: "15px", textAlign: "center", cursor: "pointer", borderBottom: activeStatus === tab ? "3px solid #00a3e0" : "3px solid transparent" }}>
                            <div style={{ fontSize: "18px", fontWeight: "bold", color: "#00a3e0" }}>{count}</div>
                            <div style={{ fontSize: "11px", color: "#666" }}>{tab === "ALL OPEN" ? "All Open" : tab.charAt(0).toUpperCase() + tab.slice(1).toLowerCase()}</div>
                        </div>
                    );
                })}
            </div>

            <div style={{ display: "flex", gap: "10px", padding: "10px 30px", backgroundColor: "#f8fafc", flexWrap: "wrap" }}>
                {[
                    { label: "Total", filter: "Total" },
                    { label: "Unassign", filter: "New" },
                    { label: "Assigned", filter: "AssignedOnly" },
                    { label: "VIP", filter: "VIP" },
                    { label: "Rush", filter: "Rush" },
                    { label: "Due", filter: "Due" },
                    { label: "Past Due", filter: "Past Due" },
                    { label: "Late", filter: "Late" }
                ].map((item) => {
                    if (item.label === "Unassign" && activeStatus !== "ALL OPEN") return null;
                    if (item.label === "Assigned" && activeStatus !== "ALL OPEN") return null;

                    const count = files.filter(file => {
                        const s = String(file.status || "").trim().toLowerCase();
                        const sub = String(file.substatus || "").trim().toLowerCase();
                        const p = String(file.priority || "").toLowerCase().trim();

                        const isException =
                            s === "wrong report" || s === "do not review" || s === "complete by client" ||
                            sub === "wrong report" || sub === "do not review" || sub === "complete by client";

                        if (!isManager && file.assigned_to !== currentUser.name) return false;

                        if (activeStatus === "ALL OPEN" && (s === "completed" || s === "rejected" || isException)) return false;
                        if (activeStatus === "ASSIGNED" && ((s !== "assigned" && s !== "working" && s !== "lender revision") || s === "completed" || s === "rejected" || isException)) return false;
                        if (activeStatus === "COMPLETED" && s !== "completed" && s !== "rejected") return false;
                        if (activeStatus === "EXCEPTIONS" && !isException) return false;

                        if (item.filter === "Total") return true;
                        if (item.filter === "New") return !file.assigned_to || String(file.assigned_to).trim() === "";
                        if (item.filter === "AssignedOnly") return file.assigned_to && String(file.assigned_to).trim() !== "";

                        if (item.filter === "VIP") return p === "vip";
                        if (item.filter === "Rush") return p.includes("rush");
                        if (item.filter === "Due") return p.includes("due date") && !p.includes("past");
                        if (item.filter === "Past Due") return p.includes("past due");
                        if (item.filter === "Late") return p.includes("late");

                        return false;
                    }).length;

                    return (
                        <button key={item.filter} onClick={() => setSubFilter(item.filter)} style={{ padding: "6px 16px", borderRadius: "20px", border: "1px solid #ddd", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", backgroundColor: subFilter === item.filter ? "#00a3e0" : "white", color: subFilter === item.filter ? "white" : "#666" }}>
                            <span style={{ fontWeight: "600", color: item.filter === "VIP" && subFilter !== "VIP" ? "#e11d48" : "inherit" }}>{item.label}</span>
                            <span style={{ backgroundColor: subFilter === item.filter ? "rgba(255,255,255,0.2)" : "#edf2f7", padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: "bold", color: item.filter === "VIP" && subFilter !== "VIP" ? "#e11d48" : "inherit" }}>{count}</span>
                        </button>
                    );
                })}
            </div>

            <div style={{ padding: "20px 30px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <div style={{ position: "relative" }}>
                        <Search size={16} style={{ position: "absolute", left: "10px", top: "10px", color: "#a0aec0" }} />
                        <input
                            type="text"
                            placeholder="Search File #, Address..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            style={{ padding: "8px 10px 8px 35px", borderRadius: "4px", border: "1px solid #ddd", fontSize: "13px", width: "220px" }}
                        />
                    </div>

                    {isManager && (
                        <button onClick={() => fileInputRef.current.click()} style={{ padding: "8px 16px", backgroundColor: "#00a3e0", color: "white", border: "none", borderRadius: "4px", display: "flex", alignItems: "center", gap: "5px", cursor: "pointer" }}><Upload size={16} /> Bulk Import</button>
                    )}
                    <button onClick={handleExport} style={{ padding: "8px 16px", backgroundColor: "#2f855a", color: "white", border: "none", borderRadius: "4px", display: "flex", alignItems: "center", gap: "5px", cursor: "pointer" }}><Download size={16} /> Export to Excel</button>
                    {isManager && (
                        <button onClick={() => setShowManualModal(true)} style={{ padding: "8px 16px", backgroundColor: "white", border: "1px solid #ddd", borderRadius: "4px", display: "flex", alignItems: "center", gap: "5px", cursor: "pointer" }}><PlusCircle size={16} /> New Entry</button>
                    )}
                    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", backgroundColor: showDuplicatesOnly ? "#fee2e2" : "white", color: showDuplicatesOnly ? "#b91c1c" : "#666", fontSize: "13px", fontWeight: "600", transition: "all 0.2s" }}>
                        <input
                            type="checkbox"
                            checked={showDuplicatesOnly}
                            onChange={(e) => {
                                setShowDuplicatesOnly(e.target.checked);
                                setCurrentPage(1);
                            }}
                            style={{ cursor: "pointer" }}
                        />
                        Show Duplicates Only
                    </label>
                </div>

                {selectedFiles.length === 0 && (
                    <div style={{ display: "flex", gap: "15px", alignItems: "center", backgroundColor: "#f8fafc", padding: "6px 14px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div style={{ width: "12px", height: "12px", backgroundColor: "#fef3c7", border: "1px solid #fcd34d", borderRadius: "3px" }} />
                            <span style={{ fontSize: "11px", fontWeight: "600", color: "#92400e" }}>Duplicate File</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div style={{ width: "12px", height: "12px", backgroundColor: "#fee2e2", border: "1px solid #ef4444", borderRadius: "3px" }} />
                            <span style={{ fontSize: "11px", fontWeight: "600", color: "#b91c1c" }}>Over SLA</span>
                        </div>
                    </div>
                )}

                {selectedFiles.length > 0 && (
                    <div style={{ display: "flex", gap: "10px", backgroundColor: "#fffaf0", padding: "8px 15px", borderRadius: "8px", border: "1px solid #feebc8", alignItems: "center" }}>
                        <span style={{ fontSize: "12px", fontWeight: "bold", color: "#9c4221" }}>{selectedFiles.length} Selected</span>

                        <select value={bulkEmployee} onChange={(e) => setBulkEmployee(e.target.value)} style={{ padding: "6px", fontSize: "12px", borderRadius: "4px" }}>
                            <option value="">Assign to...</option>
                            {dynamicEmployees.map(e => <option key={e} value={e}>{e}</option>)}
                        </select>

                        <button onClick={handleBulkAssign} style={{ padding: "6px 12px", backgroundColor: "#9c4221", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>Assign Bulk</button>
                        {isManager && (
                            <button onClick={handleBulkDelete} style={{ padding: "6px 12px", backgroundColor: "#e53e3e", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>Delete Selected</button>
                        )}
                        <X size={16} onClick={() => setSelectedFiles([])} style={{ cursor: "pointer", color: "#9c4221" }} />
                    </div>
                )}
            </div>

            <div
                ref={containerRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                style={{
                    flex: 1,
                    overflow: "auto",
                    padding: "0 30px 20px",
                    position: "relative",
                    cursor: isDragging ? "grabbing" : "default",
                    userSelect: isDragging ? "none" : "auto"
                }}
            >
                <table style={{ minWidth: "100%", borderCollapse: "collapse", backgroundColor: "white", fontSize: "12px", tableLayout: "auto" }}>
                    <thead style={{ backgroundColor: "#f8fafc", position: "sticky", top: 0, zIndex: 15 }}>
                        <tr style={{ textAlign: "center", borderBottom: "2px solid #eee" }}>
                            <th style={{
                                padding: "12px",
                                width: "40px",
                                minWidth: "40px",
                                maxWidth: "40px",
                                position: "sticky",
                                left: 0,
                                zIndex: 17,
                                backgroundColor: "#f8fafc"
                            }}>
                                <input type="checkbox" onChange={toggleSelectAll} checked={paginatedFiles.length > 0 && paginatedFiles.every(f => selectedFiles.includes(f.id || f._id))} />
                            </th>
                            <th style={{
                                padding: "12px",
                                width: "80px",
                                minWidth: "80px",
                                maxWidth: "80px",
                                position: "sticky",
                                left: "40px",
                                zIndex: 17,
                                backgroundColor: "#f8fafc"
                            }}>Actions</th>
                            {currentColumns.map((key, colIdx) => {
                                const isSticky = colIdx === 0 || colIdx === 1;

                                const stickyLeft = colIdx === 0 ? "120px" : "280px";

                                const colWidth = colIdx === 0 ? "160px" : (colIdx === 1 ? "150px" : undefined);
                                return (
                                    <th key={key} style={{
                                        padding: "12px",
                                        whiteSpace: "nowrap",
                                        textAlign: "center",
                                        position: isSticky ? "sticky" : "static",
                                        left: isSticky ? stickyLeft : undefined,
                                        zIndex: isSticky ? 17 : 15,
                                        backgroundColor: "#f8fafc",
                                        width: colWidth,
                                        minWidth: colWidth,
                                        maxWidth: colWidth
                                    }}>
                                        {toTitleCase(key)}
                                    </th>
                                );
                            })}

                        </tr>
                    </thead>

                    <tbody>
                        {paginatedFiles.map((f, i) => {
                            if (!f) return null;
                            const canEdit = isManager || (f.assigned_to && f.assigned_to === currentUser.name);
                            const startRowIndex = (currentPage - 1) * itemsPerPage;
                            const globalRowIndex = startRowIndex + i;
                            const isDup = duplicateFileNumbers.has(String(f.file_number || f.loan_number || "").trim().toLowerCase());
                            const activeColor = getActiveRowColor(f, clientTatMap);
                            const rowBgColor = activeColor ? activeColor : (selectedFiles.includes(f.id || f._id) ? "#fffaf0" : "white");

                            return (
                                <tr key={f.id || f._id || i} style={{
                                    backgroundColor: rowBgColor,
                                    borderBottom: "1px solid #f1f1f1"
                                }}>
                                    <td style={{
                                        textAlign: "center",
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 2,
                                        backgroundColor: rowBgColor,
                                        width: "40px",
                                        minWidth: "40px",
                                        maxWidth: "40px"
                                    }}>
                                        <input type="checkbox" checked={selectedFiles.includes(f.id || f._id)} onChange={() => toggleFileSelection(f.id || f._id)} />
                                    </td>

                                    <td style={{
                                        textAlign: "center",
                                        padding: "12px",
                                        position: "sticky",
                                        left: "40px",
                                        zIndex: 2,
                                        backgroundColor: rowBgColor,
                                        width: "80px",
                                        minWidth: "80px",
                                        maxWidth: "80px"
                                    }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "20px" }}>
                                            {/* Edit Pencil Icon */}
                                            <Edit
                                                size={14}
                                                color="#00a3e0"
                                                cursor="pointer"
                                                onClick={() => {
                                                    setModalFile(f);
                                                    setShowUpdateModal(true);
                                                }}
                                            />

                                            {/* Delete Trash Icon (only visible to Managers) */}
                                            {isManager && (
                                                <Trash2
                                                    size={14}
                                                    color="#e53e3e"
                                                    cursor="pointer"
                                                    onClick={() => handleDeleteClick(globalRowIndex)}
                                                />
                                            )}
                                        </div>
                                    </td>

                                    {currentColumns.map((key, colIdx) => {
                                        const isSticky = colIdx === 0 || colIdx === 1;

                                        const stickyLeft = colIdx === 0 ? "120px" : "280px";

                                        const colWidth = colIdx === 0 ? "160px" : (colIdx === 1 ? "150px" : undefined);

                                        const cellBg = isSticky ? rowBgColor : undefined;

                                        return (
                                            <td key={key} style={{
                                                padding: "0px",
                                                borderRight: "1px solid #f1f1f1",
                                                textAlign: "center",
                                                position: isSticky ? "sticky" : "static",
                                                left: isSticky ? stickyLeft : undefined,
                                                zIndex: isSticky ? 2 : 1,
                                                backgroundColor: cellBg,
                                                width: colWidth,
                                                minWidth: colWidth,
                                                maxWidth: colWidth
                                            }}>
                                                {renderCell(f, key, i, canEdit)}
                                            </td>
                                        );
                                    })}

                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div style={{ position: "fixed", bottom: "10px", left: "50%", transform: "translateX(-50%)", zIndex: 1000, display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px", backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px" }}>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: "4px 8px", minWidth: "40px", border: "1px solid #ddd", borderRadius: "3px", fontSize: "12px", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}>
                    <ChevronLeft size={16} /> Prev
                </button>

                <span style={{ fontWeight: "600" }}>Page {currentPage} of {Math.max(1, totalPages)}</span>

                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} style={{ padding: "4px 8px", minWidth: "40px", border: "1px solid #ccc", borderRadius: "3px", cursor: currentPage >= totalPages ? "not-allowed" : "pointer", opacity: currentPage >= totalPages ? 0.5 : 1 }}>
                    Next <ChevronRight size={16} />
                </button>
            </div>

            {isImporting && (
                <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.4)",
                    backdropFilter: "blur(2px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: "white",
                        padding: "32px",
                        borderRadius: "12px",
                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                        width: "360px",
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "16px"
                    }}>
                        <div style={{
                            width: "50px",
                            height: "50px",
                            border: "4px solid #f3f3f3",
                            borderTop: "4px solid #00a3e0",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite"
                        }} />
                        <style>{`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}</style>
                        <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "#1e293b" }}>
                            Importing Data
                        </h4>
                        <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
                            Please wait while we process and import your files.
                        </p>
                        <div style={{ width: "100%", backgroundColor: "#f1f5f9", borderRadius: "9999px", height: "8px", overflow: "hidden" }}>
                            <div style={{
                                width: `${importProgress}%`,
                                backgroundColor: "#00a3e0",
                                height: "100%",
                                transition: "width 0.2s ease-out",
                                borderRadius: "9999px"
                            }} />
                        </div>
                        <span style={{ fontSize: "14px", fontWeight: "600", color: "#00a3e0" }}>
                            {importProgress}%
                        </span>
                    </div>
                </div>
            )}

            {/* EDIT RECORD MODAL (Popup for updating all fields, matches structure of manual entry) */}
            <EditRecordModal
                show={showUpdateModal}
                setShow={setShowUpdateModal}
                recordData={modalFile}
                onSave={handleModalSave}
                isManager={isManager}
            />

            {/* BULK DELETE CONFIRMATION MODAL */}
            {showBulkDeleteModal && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.4)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 40000,
                    backdropFilter: "blur(2px)"
                }}>
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        width: "380px",
                        padding: "24px",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                        textAlign: "center"
                    }}>
                        <div style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "48px",
                            height: "48px",
                            borderRadius: "50%",
                            backgroundColor: "#fee2e2",
                            color: "#ef4444",
                            fontSize: "24px",
                            marginBottom: "16px"
                        }}>
                            ⚠️
                        </div>
                        <h3 style={{ margin: "0 0 8px 0", fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
                            Delete {selectedFiles.length} entries?
                        </h3>
                        <p style={{ margin: "0 0 24px 0", fontSize: "13px", color: "#64748b", lineHeight: "1.5" }}>
                            Are you sure you want to delete these {selectedFiles.length} records? This action cannot be undone.
                        </p>
                        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                            <button
                                type="button"
                                onClick={() => setShowBulkDeleteModal(false)}
                                style={{
                                    flex: 1,
                                    padding: "10px",
                                    borderRadius: "6px",
                                    border: "1px solid #cbd5e1",
                                    backgroundColor: "white",
                                    color: "#475569",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    cursor: "pointer"
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmBulkDelete}
                                style={{
                                    flex: 1,
                                    padding: "10px",
                                    borderRadius: "6px",
                                    border: "none",
                                    backgroundColor: "#ef4444",
                                    color: "white",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    cursor: "pointer"
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* DELETE CONFIRMATION MODAL */}
            {showDeleteModal && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.4)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 40000,
                    backdropFilter: "blur(2px)"
                }}>
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        width: "380px",
                        padding: "24px",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                        textAlign: "center"
                    }}>
                        <div style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "48px",
                            height: "48px",
                            borderRadius: "50%",
                            backgroundColor: "#fee2e2",
                            color: "#ef4444",
                            fontSize: "24px",
                            marginBottom: "16px"
                        }}>
                            ⚠️
                        </div>
                        <h3 style={{ margin: "0 0 8px 0", fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
                            Delete this entry?
                        </h3>
                        <p style={{ margin: "0 0 24px 0", fontSize: "13px", color: "#64748b", lineHeight: "1.5" }}>
                            Are you sure you want to delete this record? This action cannot be undone.
                        </p>
                        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                            <button
                                type="button"
                                onClick={() => { setShowDeleteModal(false); setDeleteIndex(null); }}
                                style={{
                                    flex: 1,
                                    padding: "10px",
                                    borderRadius: "6px",
                                    border: "1px solid #cbd5e1",
                                    backgroundColor: "white",
                                    color: "#475569",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    cursor: "pointer"
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmDeleteRecord}
                                style={{
                                    flex: 1,
                                    padding: "10px",
                                    borderRadius: "6px",
                                    border: "none",
                                    backgroundColor: "#ef4444",
                                    color: "white",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    cursor: "pointer"
                                }}
                            >
                                Confirm Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* REVISIONS DETAIL MODAL */}
            {revisionsModal.show && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.4)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 40000,
                    backdropFilter: "blur(2px)"
                }}>
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        width: "500px",
                        maxWidth: "90%",
                        padding: "24px",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                        display: "flex",
                        flexDirection: "column",
                        maxHeight: "80vh"
                    }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", borderBottom: "1px solid #f1f5f9", paddingBottom: "12px" }}>
                            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "20px", height: "20px", borderRadius: "50%", backgroundColor: "#0284c7", color: "white", fontSize: "12px", fontWeight: "bold" }}>i</span>
                                Revisions for File #{revisionsModal.fileNumber}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setRevisionsModal({ show: false, text: "", fileNumber: "" })}
                                style={{ border: "none", background: "none", cursor: "pointer", color: "#64748b", display: "flex", alignItems: "center", padding: "4px", borderRadius: "50%", backgroundColor: "#f1f5f9" }}
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div style={{ flex: 1, overflowY: "auto", fontSize: "13px", color: "#334155", lineHeight: "1.6", whiteSpace: "pre-line", padding: "12px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                            {revisionsModal.text}
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
                            <button
                                type="button"
                                onClick={() => setRevisionsModal({ show: false, text: "", fileNumber: "" })}
                                style={{
                                    padding: "8px 20px",
                                    borderRadius: "6px",
                                    border: "1px solid #cbd5e1",
                                    backgroundColor: "white",
                                    color: "#475569",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    cursor: "pointer"
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* SLEEK TOAST NOTIFICATION PORTAL */}
            {toast && (
                <div style={{
                    position: "fixed",
                    bottom: "24px",
                    right: "24px",
                    backgroundColor: toast.type === "error" ? "#fee2e2" : "#ecfdf5",
                    border: toast.type === "error" ? "1px solid #fecaca" : "1px solid #a7f3d0",
                    borderRadius: "8px",
                    padding: "14px 20px",
                    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                    zIndex: 50000,
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    maxWidth: "380px",
                    animation: "slideInToast 0.3s ease-out"
                }}>
                    <span style={{ fontSize: "16px" }}>
                        {toast.type === "error" ? "❌" : "✅"}
                    </span>
                    <div style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: toast.type === "error" ? "#991b1b" : "#065f46",
                        lineHeight: "1.4"
                    }}>
                        {toast.message}
                    </div>
                </div>
            )}

            <style>
                {`
                    @keyframes slideInToast {
                        from { transform: translateY(100%) scale(0.9); opacity: 0; }
                        to { transform: translateY(0) scale(1); opacity: 1; }
                    }
                    .revisions-badge-btn:hover {
                        background-color: #bae6fd !important;
                        transform: translateY(-1px);
                        box-shadow: 0 2px 4px rgba(2, 132, 199, 0.1);
                    }
                    /* Explicitly allow mouse text selection in the table */
                    table, th, td {
                        user-select: text !important;
                        -webkit-user-select: text !important;
                        -moz-user-select: text !important;
                        -ms-user-select: text !important;
                    }
                `}
            </style>
        </div>
    );
};

export default FileTable;
